import axios from '../api';

/**
 * PDF Export Service
 * Handles medical record PDF export functionality
 */

/**
 * Export medical record PDF for a patient
 * @param {string} patientId - Patient ID
 * @param {Object} healthData - Optional health data (wellness score, health summary)
 * @returns {Promise<{blob: Blob, filename: string}>} PDF file blob and filename from server
 */
export const exportMedicalRecordPDF = async (patientId, healthData = {}) => {
  try {
    const response = await axios.post(
      `/patients/${patientId}/export-medical-record`,
      healthData,
      {
        responseType: 'blob', // Important: Tell axios to expect binary data
        timeout: 180000 // Allow up to 3 minutes for PDF generation/streaming
      }
    );

    // Check if response is actually a PDF (not an error response)
    if (response.data.type === 'application/json') {
      // If server returned JSON error as blob, parse it
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.message || 'PDF generation failed');
    }

    // Extract filename from Content-Disposition header
    let filename = 'MedicalRecord.pdf'; // Default fallback
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    return { blob: response.data, filename };
  } catch (error) {
    console.error('Error exporting medical record PDF:', error);

    // Try to extract error message from blob response
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        error.message = errorData.message || error.message;
      } catch (e) {
        // If can't parse, use original error
      }
    }

    throw error;
  }
};

/**
 * Preview medical record data (without generating PDF)
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Patient data object
 */
export const previewMedicalRecordData = async (patientId) => {
  try {
    const response = await axios.get(`/patients/${patientId}/export-preview`);
    return response.data.data;
  } catch (error) {
    console.error('Error previewing medical record data:', error);
    throw error;
  }
};

/**
 * Get export status (for potential async implementation)
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Export status object
 */
export const getExportStatus = async (patientId) => {
  try {
    const response = await axios.get(`/patients/${patientId}/export-status`);
    return response.data;
  } catch (error) {
    console.error('Error getting export status:', error);
    throw error;
  }
};

/**
 * Download PDF file (helper function)
 * @param {Blob} blob - PDF blob data
 * @param {string} filename - Filename for download
 */
export const downloadPDF = (blob, filename = 'medical_record.pdf') => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};
